import { motion, AnimatePresence } from 'framer-motion'
import { X, BookOpen, Clock, Users, Star, Play, Award, CheckCircle, Download, ArrowRight } from 'lucide-react'
import { GlassmorphicCard } from './glassmorphic-card'
import { useAuth } from '@/context/auth-context'
import { useNavigate } from 'react-router-dom'

interface CourseModalProps {
    course: {
        id: string
        title: string
        instructor: string
        students: number
        rating: number
        reviews: number
        duration: string
        lectures: number
        price: string
        originalPrice: string
        category: string
        level: string
        badge?: string
        features: string[]
        lastUpdated: string
    } | null
    isOpen: boolean
    onClose: () => void
}

export function CourseModal({ course, isOpen, onClose }: CourseModalProps) {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()

    if (!course) return null

    const curriculum = [
        { module: 'Module 1: Fundamentals', lectures: 12, duration: '3 hours' },
        { module: 'Module 2: Core Concepts', lectures: 15, duration: '4 hours' },
        { module: 'Module 3: Advanced Topics', lectures: 18, duration: '5 hours' },
        { module: 'Module 4: Practice & Revision', lectures: 10, duration: '2.5 hours' },
    ]

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    const handleEnroll = () => {
        if (isAuthenticated) {
            navigate('/checkout')
        } else {
            navigate('/login')
        }
        onClose()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleBackdropClick}
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-0 md:p-4 overflow-y-auto"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-4xl md:my-8 h-full md:h-auto"
                    >
                        <GlassmorphicCard className="relative overflow-hidden flex flex-col h-full md:h-auto max-h-none md:max-h-[90vh]">
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-100/50 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700 text-theme-primary transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>

                            {/* Course Header Image */}
                            <div className="relative h-48 md:h-64 bg-gradient-to-br from-primary-blue/30 via-purple-500/20 to-accent-gold/30 flex items-center justify-center overflow-hidden shrink-0">
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent" />
                                <BookOpen className="h-16 w-16 md:h-24 md:w-24 text-primary-blue/50 relative z-10" />
                                {course.badge && (
                                    <span className="absolute top-4 right-4 px-4 py-2 bg-accent-gold text-slate-900 text-sm font-bold rounded-full">
                                        {course.badge}
                                    </span>
                                )}
                                <span className="absolute top-4 left-4 px-4 py-2 bg-primary-blue/90 text-white text-sm font-semibold rounded-full">
                                    {course.level}
                                </span>
                            </div>

                            {/* Content */}
                            <div className="p-4 md:p-8 overflow-y-auto flex-1 md:flex-initial md:max-h-[60vh]">
                                {/* Category */}
                                <span className="inline-block px-3 py-1 bg-primary-blue/20 text-primary-blue text-sm font-semibold rounded-full mb-4">
                                    {course.category}
                                </span>

                                {/* Title */}
                                <h2 className="text-2xl md:text-3xl font-display font-bold text-theme-primary mb-3">
                                    {course.title}
                                </h2>

                                {/* Instructor */}
                                <p className="text-theme-secondary mb-4">{course.instructor}</p>

                                {/* Stats */}
                                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 pb-6 border-b border-slate-200 dark:border-slate-700/50">
                                    <div className="flex items-center gap-2">
                                        <Star className="h-5 w-5 fill-accent-gold text-accent-gold" />
                                        <span className="font-semibold text-theme-primary">{course.rating}</span>
                                        <span className="text-theme-muted">({course.reviews} reviews)</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-theme-secondary">
                                        <Users className="h-5 w-5" />
                                        <span>{course.students.toLocaleString()} students</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-theme-secondary">
                                        <Play className="h-5 w-5" />
                                        <span>{course.lectures} lectures</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-theme-secondary">
                                        <Clock className="h-5 w-5" />
                                        <span>{course.duration}</span>
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="mb-6">
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-4">
                                        What You'll Get
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {course.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-start gap-2">
                                                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-theme-secondary">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Curriculum */}
                                <div className="mb-6">
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-4">
                                        Course Curriculum
                                    </h3>
                                    <div className="space-y-3">
                                        {curriculum.map((module, idx) => (
                                            <div
                                                key={idx}
                                                className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/30 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors gap-2 md:gap-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-primary-blue/20 text-primary-blue flex items-center justify-center font-semibold text-sm shrink-0">
                                                        {idx + 1}
                                                    </div>
                                                    <span className="font-medium text-theme-primary">{module.module}</span>
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-theme-muted ml-11 md:ml-0">
                                                    <span>{module.lectures} lectures</span>
                                                    <span>{module.duration}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Instructor Info */}
                                <div className="mb-6 p-6 bg-slate-50 dark:bg-slate-800/30 rounded-lg">
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-3">
                                        About the Instructor
                                    </h3>
                                    <p className="text-theme-secondary mb-3">{course.instructor}</p>
                                    <div className="flex items-center gap-4 text-sm text-theme-muted">
                                        <div className="flex items-center gap-1">
                                            <Award className="h-4 w-4 text-accent-gold" />
                                            <span>Expert Educator</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Users className="h-4 w-4" />
                                            <span>{course.students.toLocaleString()}+ students taught</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Last Updated */}
                                <p className="text-sm text-theme-muted mb-6">
                                    Last updated: {course.lastUpdated}
                                </p>
                            </div>

                            {/* Footer */}
                            <div className="p-4 md:p-6 border-t border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/20 shrink-0">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="w-full md:w-auto text-center md:text-left">
                                        <div className="flex items-baseline justify-center md:justify-start gap-3 mb-1">
                                            <span className="text-3xl font-bold gradient-text">{course.price}</span>
                                            <span className="text-lg text-theme-muted line-through">{course.originalPrice}</span>
                                        </div>
                                        <p className="text-sm text-green-500 font-medium">
                                            Save {Math.round((1 - parseInt(course.price.replace(/[^0-9]/g, '')) / parseInt(course.originalPrice.replace(/[^0-9]/g, ''))) * 100)}%
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                                        <button className="flex-1 md:flex-none px-6 py-3 border-2 border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                                            <Download className="h-5 w-5" />
                                            Download Syllabus
                                        </button>
                                        <button
                                            onClick={handleEnroll}
                                            className="flex-1 md:flex-none px-6 py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2"
                                        >
                                            Enroll Now
                                            <ArrowRight className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
